import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Container,
  Divider,
  IconButton,
  Typography
} from '@material-ui/core';
import usePageTitle from '../../hooks/usePageTitle';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useHistory } from 'react-router-dom';
import { APP_PATH_SIGNUP } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.background.default
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  lastUpdated: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    lineHeight: 1.7
  },
  list: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4)
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    lineHeight: 1.7
  },
  contactEmail: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  goBack: {
    marginBottom: theme.spacing(2)
  }
}));

const TermsAndConditions = () => {
  const classes = useStyles();
  usePageTitle('Terms and Conditions');
  const history = useHistory();
  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        <IconButton
          className={classes.goBack}
          onClick={() => history.push(APP_PATH_SIGNUP)}>
          <ArrowBackIcon />
        </IconButton>
        <Box className={classes.content}>
          <Typography variant="h2" className={classes.title}>
            Terms and Conditions
          </Typography>
          <Typography className={classes.lastUpdated}>
            Last updated: February 2, 2026
          </Typography>
          <Typography className={classes.paragraph}>
            Welcome to Remote Job, a platform operated by IBunker LLC
            (“Company”, “we”, “us”, or “our”). These Terms and Conditions
            (“Terms”) govern your access to and use of the Remote Job website,
            platform, applications, tools, and services (collectively, the
            “Service”).
          </Typography>
          <Typography>
            By accessing or using Remote Job, you agree to be bound by these
            Terms. If you do not agree, you may not use the Service.
          </Typography>
          <Typography className={classes.paragraph}>
            These Terms apply to all users, including job seekers, companies,
            startups, enterprises, and visitors.
          </Typography>
          <Divider />
          {/* Section 1 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            1. About Remote Job
          </Typography>
          <Typography className={classes.paragraph}>
            Remote Job is a technology platform designed to help companies
            discover, manage, and connect with remote talent, and to help
            professionals find and apply for remote opportunities.
          </Typography>
          <Typography className={classes.paragraph}>Remote Job:</Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Is <strong>not</strong> an employer, staffing agency, or recruiter
            </li>
            <li className={classes.listItem}>
              Does <strong>not</strong> make hiring decisions
            </li>
            <li className={classes.listItem}>
              Does not guarantee employment, interviews, or outcomes
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            We provide tools, workflows, and services that support the hiring
            process, but{' '}
            <strong>
              all employment relationships are solely between companies and
              candidates
            </strong>
            .
          </Typography>
          <Divider />
          {/* Section 2 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            2. Eligibility
          </Typography>
          <Typography className={classes.paragraph}>
            By using the Service, you confirm that:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              You are at least <strong>18 years old</strong>
            </li>
            <li className={classes.listItem}>
              You have the legal capacity to enter into binding agreements
            </li>
            <li className={classes.listItem}>
              All information you provide is accurate and up to date
            </li>
            <li className={classes.listItem}>
              If you represent a company, you are authorized to act on its
              behalf
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Remote Job reserves the right to restrict or terminate access if
            eligibility requirements are not met.
          </Typography>
          <Divider />
          {/* Section 3 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            3. User Accounts
          </Typography>
          <Typography className={classes.paragraph}>
            Some features require account registration.
          </Typography>
          <Typography className={classes.paragraph}>You agree to:</Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Provide accurate and complete information
            </li>
            <li className={classes.listItem}>Keep your credentials secure</li>
            <li className={classes.listItem}>
              Be responsible for all activity under your account
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Remote Job may suspend or terminate accounts that:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>Violate these Terms</li>
            <li className={classes.listItem}>Misuse the platform</li>
            <li className={classes.listItem}>
              Engage in fraudulent or abusive behavior
            </li>
          </ul>
          <Divider />
          {/* Section 4 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            4. Platform Services
          </Typography>
          <Typography className={classes.paragraph}>
            Remote Job may offer, now or in the future:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Job posting and distribution tools
            </li>
            <li className={classes.listItem}>
              Candidate and application management
            </li>
            <li className={classes.listItem}>
              Messaging and communication features
            </li>
            <li className={classes.listItem}>
              Interview scheduling and workflow tools
            </li>
            <li className={classes.listItem}>Analytics and hiring insights</li>
            <li className={classes.listItem}>
              AI-assisted features (e.g. screening, recommendations, automation)
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Features may vary by plan and may change over time.
          </Typography>
          <Typography className={classes.paragraph}>
            AI-assisted features are provided for informational and workflow
            support purposes only. Outputs may be automated, probabilistic, and
            may require human review. Remote Job does not guarantee the
            accuracy, completeness, or suitability of AI-generated outputs, and
            users remain responsible for all decisions made using such features.
          </Typography>
          <Divider />
          {/* Section 5 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            5. Job Listings & Applications
          </Typography>
          <Typography
            variant="h6"
            style={{ marginTop: '16px', marginBottom: '8px', fontWeight: 600 }}>
            For Companies
          </Typography>
          <Typography className={classes.paragraph}>
            You are responsible for:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              The accuracy, legality, and content of job postings
            </li>
            <li className={classes.listItem}>
              Compliance with applicable labor, employment, and
              anti-discrimination laws
            </li>
            <li className={classes.listItem}>
              Ensuring listings are genuine and not misleading
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            You may not post:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Scams, fake jobs, or deceptive offers
            </li>
            <li className={classes.listItem}>
              Discriminatory or unlawful content
            </li>
            <li className={classes.listItem}>Spam or irrelevant listings</li>
          </ul>
          <Typography
            variant="h6"
            style={{ marginTop: '16px', marginBottom: '8px', fontWeight: 600 }}>
            For Candidates
          </Typography>
          <Typography className={classes.paragraph}>You agree that:</Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Profile and application information is truthful
            </li>
            <li className={classes.listItem}>
              Remote Job does not guarantee interviews or job offers
            </li>
            <li className={classes.listItem}>
              Hiring decisions are made solely by companies
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Remote Job is not responsible for employer actions, communications,
            or outcomes.
          </Typography>
          <Divider />
          {/* Section 6 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            6. Payments, Plans & Fees
          </Typography>
          <Typography className={classes.paragraph}>
            Certain features require payment, including but not limited to:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>Job postings</li>
            <li className={classes.listItem}>Hiring plans or bundles</li>
            <li className={classes.listItem}>Premium or advanced tools</li>
          </ul>
          <Typography className={classes.paragraph}>
            By purchasing a paid service, you agree to:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Pay all applicable fees and taxes
            </li>
            <li className={classes.listItem}>
              Provide valid payment information
            </li>
            <li className={classes.listItem}>
              Acknowledge that fees are <strong>non-refundable</strong>, unless
              explicitly stated otherwise
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Remote Job may modify pricing, features, or plans at any time.
          </Typography>
          <Divider />
          {/* Section 7 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            7. Acceptable Use
          </Typography>
          <Typography className={classes.paragraph}>
            You agree not to:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Use the Service for illegal or fraudulent purposes
            </li>
            <li className={classes.listItem}>
              Abuse, harass, or discriminate against others
            </li>
            <li className={classes.listItem}>
              Scrape, copy, or resell platform data without authorization
            </li>
            <li className={classes.listItem}>
              Attempt to disrupt or compromise system security
            </li>
            <li className={classes.listItem}>
              Reverse-engineer or misuse platform features
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Violations may result in immediate suspension or termination.
          </Typography>
          <Divider />
          {/* Section 8 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            8. Intellectual Property
          </Typography>
          <Typography className={classes.paragraph}>
            All Remote Job content, including:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>Software</li>
            <li className={classes.listItem}>Design</li>
            <li className={classes.listItem}>Branding</li>
            <li className={classes.listItem}>Logos</li>
            <li className={classes.listItem}>Platform features</li>
          </ul>
          <Typography className={classes.paragraph}>
            is owned by or licensed to Remote Job and protected by intellectual
            property laws.
          </Typography>
          <Typography className={classes.paragraph}>
            Users retain ownership of the content they submit but grant Remote
            Job a <strong>non-exclusive, worldwide, royalty-free</strong>{' '}
            license to host, display, process, and use that content solely for
            the operation, improvement, and promotion of the Service.
          </Typography>
          <Divider />
          {/* Section 9 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            9. Privacy & Data
          </Typography>
          <Typography className={classes.paragraph}>
            Your use of the Service is governed by our Privacy Policy, which
            explains how we collect, use, and protect personal data.
          </Typography>
          <Typography className={classes.paragraph}>
            By using Remote Job, you consent to our data practices.
          </Typography>
          <Divider />
          {/* Section 10 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            10. Disclaimer of Warranties
          </Typography>
          <Typography className={classes.paragraph}>
            The Service is provided <strong>“as is” and “as available.”</strong>
          </Typography>
          <Typography className={classes.paragraph}>
            Remote Job does not guarantee:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Job placement or hiring success
            </li>
            <li className={classes.listItem}>
              Candidate quality or employer suitability
            </li>
            <li className={classes.listItem}>
              Continuous or error-free availability
            </li>
            <li className={classes.listItem}>
              That users will meet expectations
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Use of the platform is at your own risk.
          </Typography>
          <Divider />
          {/* Section 11 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            11. Limitation of Liability
          </Typography>
          <Typography className={classes.paragraph}>
            To the maximum extent permitted by law, Remote Job shall not be
            liable for:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>Employment disputes</li>
            <li className={classes.listItem}>Lost opportunities or profits</li>
            <li className={classes.listItem}>User-generated content</li>
            <li className={classes.listItem}>
              Indirect, incidental, or consequential damages
            </li>
          </ul>
          <Typography className={classes.paragraph}>
            Our total liability shall not exceed the amount paid (if any) for
            use of the Service.
          </Typography>
          <Divider />
          {/* Section 12 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            12. Termination
          </Typography>
          <Typography className={classes.paragraph}>
            You may stop using the Service at any time.
          </Typography>
          <Typography className={classes.paragraph}>
            Remote Job may suspend or terminate access:
          </Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>For violations of these Terms</li>
            <li className={classes.listItem}>To protect platform integrity</li>
            <li className={classes.listItem}>
              To comply with legal obligations
            </li>
          </ul>
          <Divider />
          {/* Section 13 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            13. Changes to These Terms
          </Typography>
          <Typography className={classes.paragraph}>
            We may update these Terms periodically.
          </Typography>
          <Typography className={classes.paragraph}>
            Continued use of the Service after changes become effective
            constitutes acceptance of the updated Terms.
          </Typography>
          <Divider />
          {/* Section 14 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            14. Governing Law
          </Typography>
          <Typography className={classes.paragraph}>
            These Terms are governed by applicable laws, without regard to
            conflict-of-law principles.
          </Typography>
          <Divider />
          {/* Section 15 */}
          <Typography variant="h4" className={classes.sectionTitle}>
            15. Contact Us
          </Typography>
          <Typography className={classes.paragraph}>
            Remote Job is operated by IBunker LLC.
          </Typography>
          <Typography className={classes.paragraph}>
            <span role="img" aria-label="email">
              📧
            </span>{' '}
            <a
              href="mailto:support@remote-job.ai"
              className={classes.contactEmail}>
              support@remote-job.ai
            </a>
          </Typography>
        </Box>
        <IconButton
          className={classes.goBack}
          onClick={() => history.push(APP_PATH_SIGNUP)}>
          <ArrowBackIcon />
        </IconButton>
      </Container>
    </div>
  );
};

export default TermsAndConditions;
